<template>
    <div v-if="cards.length" class="floating-subscription" :class="{ 'is-mobile': isMobile, 'is-collapsed': !panelOpen }">
        <transition name="floating-subscription-pop">
            <div v-if="panelOpen" class="floating-subscription-stack" :class="{ 'is-mobile': isMobile }">
                <div
                    v-for="card in cards"
                    :key="card.key"
                    class="floating-subscription-card"
                    :class="{ 'is-mobile': isMobile, 'is-mini-program': card.key === 'miniProgram' }"
                >
                    <div class="floating-subscription-badge">{{ card.badge }}</div>
                    <div class="floating-subscription-header">
                        <div class="floating-subscription-title">{{ card.title }}</div>
                    </div>
                    <div class="floating-subscription-qr-wrap">
                        <img class="floating-subscription-qr" :src="card.qrImage" :alt="card.title">
                    </div>
                </div>
            </div>
        </transition>
        <button
            type="button"
            class="floating-subscription-toggle"
            :class="{ 'is-mobile': isMobile, 'is-collapsed': !panelOpen }"
            :aria-label="panelOpen ? '隐藏订阅入口' : '显示订阅入口'"
            @click="toggleWidget"
        >
            <svg v-if="panelOpen" class="floating-subscription-toggle__icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M1.5 12s3.8-6.5 10.5-6.5S22.5 12 22.5 12 18.7 18.5 12 18.5 1.5 12 1.5 12Z" />
                <circle cx="12" cy="12" r="3.2" />
            </svg>
            <svg v-else class="floating-subscription-toggle__icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M3 3l18 18" />
                <path d="M10.6 5.7A11.8 11.8 0 0 1 12 5.5c6.7 0 10.5 6.5 10.5 6.5a18.8 18.8 0 0 1-4 4.6" />
                <path d="M14.1 14.1A3.2 3.2 0 0 1 9.9 9.9" />
                <path d="M6.2 6.2A18.3 18.3 0 0 0 1.5 12s3.8 6.5 10.5 6.5a11.7 11.7 0 0 0 5.8-1.5" />
            </svg>
        </button>
    </div>
</template>

<script>
import {
    SUBSCRIPTION_FOLLOW_ENABLED,
    SUBSCRIPTION_FOLLOW_QR_IMAGE,
    SUBSCRIPTION_FOLLOW_TITLE,
    MINIPROGRAM_WIDGET_ENABLED,
    MINIPROGRAM_WIDGET_QR_IMAGE,
    MINIPROGRAM_WIDGET_TITLE
} from '@/config'

export default {
    name: 'FloatingSubscriptionWidget',
    data() {
        return {
            isMobile: false,
            panelOpen: true
        }
    },
    computed: {
        cards() {
            const out = []
            if (SUBSCRIPTION_FOLLOW_ENABLED && SUBSCRIPTION_FOLLOW_QR_IMAGE) {
                out.push({
                    key: 'subscription',
                    badge: '订阅号',
                    title: SUBSCRIPTION_FOLLOW_TITLE,
                    qrImage: SUBSCRIPTION_FOLLOW_QR_IMAGE
                })
            }
            if (MINIPROGRAM_WIDGET_ENABLED && MINIPROGRAM_WIDGET_QR_IMAGE) {
                out.push({
                    key: 'miniProgram',
                    badge: '小程序',
                    title: MINIPROGRAM_WIDGET_TITLE,
                    qrImage: MINIPROGRAM_WIDGET_QR_IMAGE
                })
            }
            return out
        }
    },
    mounted() {
        this.syncViewport()
        this._resizeHandler = () => this.syncViewport()
        window.addEventListener('resize', this._resizeHandler)
    },
    beforeDestroy() {
        if (this._resizeHandler) window.removeEventListener('resize', this._resizeHandler)
    },
    methods: {
        syncViewport() {
            this.isMobile = typeof window !== 'undefined' && window.innerWidth <= 768
        },
        toggleWidget() {
            this.panelOpen = !this.panelOpen
        }
    }
}
</script>

<style lang="less" scoped>
.floating-subscription {
    position: fixed;
    right: 18px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1400;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 12px;
}

.floating-subscription.is-mobile {
    right: 14px;
    top: auto;
    bottom: calc(env(safe-area-inset-bottom, 0px) + 18px);
    transform: none;
}

.floating-subscription-stack {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.floating-subscription-stack.is-mobile {
    gap: 10px;
}

.floating-subscription-card {
    position: relative;
    width: 166px;
    padding: 16px 14px 14px;
    border-radius: 24px;
    background:
        radial-gradient(circle at top right, rgba(255, 255, 255, 0.96), rgba(255, 255, 255, 0.9) 45%, rgba(242, 252, 244, 0.96) 100%);
    border: 1px solid rgba(103, 194, 58, 0.22);
    box-shadow:
        0 18px 50px rgba(35, 51, 28, 0.18),
        0 0 0 1px rgba(255, 255, 255, 0.6) inset;
    backdrop-filter: blur(12px);
}

.floating-subscription-card.is-mini-program {
    background:
        radial-gradient(circle at top right, rgba(255, 255, 255, 0.96), rgba(255, 255, 255, 0.9) 45%, rgba(240, 248, 255, 0.96) 100%);
    border-color: rgba(64, 158, 255, 0.22);
}

.floating-subscription-card::before {
    content: '';
    position: absolute;
    inset: -8px;
    border-radius: 30px;
    border: 1px solid rgba(103, 194, 58, 0.14);
    animation: floating-subscription-glow 2.8s ease-in-out infinite;
    pointer-events: none;
}

.floating-subscription-card.is-mini-program::before {
    border-color: rgba(64, 158, 255, 0.16);
}

.floating-subscription-badge {
    display: inline-flex;
    align-items: center;
    height: 26px;
    padding: 0 10px;
    margin-bottom: 12px;
    border-radius: 999px;
    background: linear-gradient(135deg, #67c23a, #8bd45a);
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.5px;
}

.floating-subscription-card.is-mini-program .floating-subscription-badge {
    background: linear-gradient(135deg, #409eff, #63b3ff);
}

.floating-subscription-header {
    margin-bottom: 10px;
}

.floating-subscription-title {
    font-size: 13px;
    line-height: 1.2;
    font-weight: 700;
    color: #203014;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.floating-subscription-qr-wrap {
    padding: 10px;
    border-radius: 18px;
    background: #fff;
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
}

.floating-subscription-qr {
    display: block;
    width: 100%;
    border-radius: 12px;
}

.floating-subscription-toggle {
    width: 44px;
    height: 44px;
    border: 0;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #67c23a, #409eff);
    box-shadow: 0 14px 34px rgba(42, 92, 146, 0.24);
    color: #fff;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.floating-subscription-toggle:hover {
    transform: translateY(-1px);
    box-shadow: 0 18px 38px rgba(42, 92, 146, 0.28);
}

.floating-subscription-toggle__icon {
    width: 20px;
    height: 20px;
    stroke: currentColor;
    stroke-width: 1.9;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
}

.floating-subscription-toggle.is-mobile {
    width: 46px;
    height: 46px;
}

.floating-subscription-toggle.is-collapsed {
    opacity: 0.94;
}

.floating-subscription-card.is-mobile {
    width: 168px;
    padding: 14px 12px 12px;
}

.floating-subscription-pop-enter-active,
.floating-subscription-pop-leave-active {
    transition: all 0.22s ease;
}

.floating-subscription-pop-enter,
.floating-subscription-pop-leave-to {
    opacity: 0;
    transform: translateY(10px) scale(0.96);
}

@keyframes floating-subscription-glow {
    0%, 100% {
        opacity: 0.45;
        transform: scale(1);
    }
    50% {
        opacity: 0.8;
        transform: scale(1.02);
    }
}

.dark-mode .floating-subscription-card {
    background:
        radial-gradient(circle at top right, rgba(31, 45, 28, 0.96), rgba(25, 36, 23, 0.94) 42%, rgba(18, 28, 18, 0.97) 100%);
    border-color: rgba(103, 194, 58, 0.22);
    box-shadow:
        0 22px 60px rgba(0, 0, 0, 0.42),
        0 0 0 1px rgba(255, 255, 255, 0.04) inset;
}

.dark-mode .floating-subscription-card.is-mini-program {
    background:
        radial-gradient(circle at top right, rgba(20, 36, 48, 0.96), rgba(17, 28, 38, 0.94) 42%, rgba(12, 21, 30, 0.97) 100%);
    border-color: rgba(64, 158, 255, 0.22);
}

.dark-mode .floating-subscription-title {
    color: #e8f6de;
}

.dark-mode .floating-subscription-qr-wrap {
    background: #fff;
}

.dark-mode .floating-subscription-toggle {
    box-shadow: 0 16px 36px rgba(0, 0, 0, 0.36);
}
</style>
